import { getFeedPageState, getNudgeManageState, ROUTE_PATHS } from '@config/routes';
import { FEED_TAB_IDS } from '@modules/merchant/feed/constants/feedTabs';
import { fetchRestaurants } from '@redux/merchant/merchantSlice';

export const NOTIFICATION_EVENT_TYPES = {
  MERCHANT_NUDGE_RSVP: 'MERCHANT_NUDGE_RSVP',
  MERCHANT_FEED_LIKED: 'MERCHANT_FEED_LIKED',
  MERCHANT_FEED_COMMENTED: 'MERCHANT_FEED_COMMENTED',
  RESTAURANT_TEAM_PERMISSIONS_UPDATED: 'RESTAURANT_TEAM_PERMISSIONS_UPDATED',
  RESTAURANT_OWNERSHIP_TRANSFERRED: 'RESTAURANT_OWNERSHIP_TRANSFERRED',
  RESTAURANT_OWNERSHIP_APPROVED: 'RESTAURANT_OWNERSHIP_APPROVED',
  // RESTAURANT_CLAIM_REJECTED: 'RESTAURANT_CLAIM_REJECTED',
  MERCHANT_RESTAURANT_FOLLOWED: 'MERCHANT_RESTAURANT_FOLLOWED',
  RESTAURANT_TEAM_MEMBER_ADDED: 'RESTAURANT_TEAM_MEMBER_ADDED',
};

const pickText = (...values) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text.toLowerCase() !== 'null' && text.toLowerCase() !== 'undefined') {
      return text;
    }
  }
  return '';
};

/**
 * Normalizes list-API items and push payloads into a common shape for routing.
 */
export const normalizeNotificationPayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const nestedData =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : null;
  const root = nestedData ? { ...raw, ...nestedData } : raw;

  const notificationData =
    root.notificationData && typeof root.notificationData === 'object'
      ? root.notificationData
      : root;

  return {
    id: pickText(root.id, root.notificationId),
    eventType: pickText(
      root.eventType,
      notificationData.notificationType,
      notificationData.eventType,
    ),
    restaurantId: pickText(
      root.restaurantId,
      root.restaurant_id,
      notificationData.restaurantId,
      notificationData.restaurant_id,
    ),
    nudgeId: pickText(
      root.nudgeId,
      root.nudge_id,
      notificationData.nudgeId,
      notificationData.nudge_id,
    ),
    feedId: pickText(root.feedId, root.feed_id, notificationData.feedId, notificationData.feed_id),
    followerId: pickText(
      notificationData.id,
      notificationData.followerUserId,
      notificationData.follower_user_id,
      notificationData.followerId,
      notificationData.follower_id,
      notificationData.userId,
      notificationData.user_id,
      root.followerUserId,
      root.follower_user_id,
      root.followerId,
      root.follower_id,
      root.userId,
      root.user_id,
    ),
    notificationData,
  };
};

/**
 * Maps a merchant notification (list item or push payload) to an in-app navigation target.
 * Returns null when the event has no dedicated destination.
 */
export const resolveNotificationNavigation = (notification) => {
  const normalized = normalizeNotificationPayload(notification);
  if (!normalized) return null;

  const { eventType, restaurantId, nudgeId } = normalized;

  // Any nudge-related notification with a nudgeId opens Nudge Details.
  if (nudgeId) {
    return {
      path: ROUTE_PATHS.NUDGE_MANAGE,
      state: getNudgeManageState({ id: nudgeId }, restaurantId),
      selectBusiness: Boolean(restaurantId),
      restaurantId,
    };
  }

  switch (eventType) {
    case NOTIFICATION_EVENT_TYPES.MERCHANT_NUDGE_RSVP:
      return {
        path: ROUTE_PATHS.NUDGES,
        selectBusiness: Boolean(restaurantId),
        restaurantId,
      };

    case NOTIFICATION_EVENT_TYPES.MERCHANT_FEED_LIKED:
    case NOTIFICATION_EVENT_TYPES.MERCHANT_FEED_COMMENTED:
      return {
        path: ROUTE_PATHS.FEED,
        selectBusiness: Boolean(restaurantId),
        restaurantId,
      };

    case NOTIFICATION_EVENT_TYPES.MERCHANT_RESTAURANT_FOLLOWED:
      return {
        path: ROUTE_PATHS.FEED,
        state: getFeedPageState({ feedTab: FEED_TAB_IDS.followers }),
        selectBusiness: Boolean(restaurantId),
        restaurantId,
      };

    case NOTIFICATION_EVENT_TYPES.RESTAURANT_TEAM_PERMISSIONS_UPDATED:
    case NOTIFICATION_EVENT_TYPES.RESTAURANT_OWNERSHIP_TRANSFERRED:
    case NOTIFICATION_EVENT_TYPES.RESTAURANT_OWNERSHIP_APPROVED:
      // case NOTIFICATION_EVENT_TYPES.RESTAURANT_CLAIM_REJECTED:
      return {
        path: ROUTE_PATHS.SETUP_GUIDE,
        selectBusiness: true,
        restaurantId,
      };
    case NOTIFICATION_EVENT_TYPES.RESTAURANT_TEAM_MEMBER_ADDED:
      return {
        path: ROUTE_PATHS.TEAM,
        selectBusiness: Boolean(restaurantId),
        restaurantId,
      };
    default:
      return null;
  }
};

/**
 * Applies resolved notification navigation (business switch + route).
 * Shared by notification list clicks and push-notification deep links.
 * Refreshes the restaurant list before switching so newly granted businesses
 * (e.g. RESTAURANT_TEAM_MEMBER_ADDED) resolve approval status before the nav paints.
 */
export const applyNotificationNavigation = async ({
  notification,
  navigate,
  dispatch,
  activeBusinessId,
  switchBusiness,
}) => {
  if (!navigate) return false;

  const navigation = resolveNotificationNavigation(notification);
  if (!navigation?.path) return false;

  const restaurantId = navigation.restaurantId;
  const needsBusinessSwitch =
    Boolean(restaurantId) &&
    String(restaurantId) !== String(activeBusinessId ?? '') &&
    typeof switchBusiness === 'function' &&
    Boolean(dispatch);

  if (needsBusinessSwitch) {
    // Start list refresh before switching so `businessesLoading` is true when an
    // optimistic snapshot (no status yet) would otherwise flash the limited nav.
    const fetchPromise = dispatch(fetchRestaurants(''));
    dispatch(switchBusiness(restaurantId));
    await fetchPromise;
  }

  if (navigation.state) {
    navigate(navigation.path, { state: navigation.state });
  } else {
    navigate(navigation.path);
  }

  return true;
};

export default resolveNotificationNavigation;
